import { useDbClient, useLiveQuery } from "@tanstack/react-db";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { QueryRequestContext, collectionError } from "../../../data/index";
import {
  replayLaunchCollection,
  replayReadinessCollection,
} from "./replay-session.collections";
import {
  discardUnavailableReplayLaunch,
  launchReplaySession,
} from "./replay-session.mutations";
import { type ReplayTimingController } from "../playback/use-replay-timing";
import { freshReplayTiming, replayTerminals } from "../playback/replay-timing";
import {
  replayLaunchTiming,
  replayReadinessFailure,
} from "./replay-session-presentation";

import { useReplayLetterbox } from "./use-replay-letterbox";

export function useReplaySession({
  coworldId,
  replayUri,
  letterbox,
  telemetry,
  setError,
}: Readonly<{
  coworldId: string;
  replayUri: string;
  letterbox?: "page" | undefined;
  telemetry?: ReplayTimingController;
  setError?: (message: string) => void;
}>) {
  const db = useDbClient();
  const { elevatedPrivileges, reportAuthorizationError } =
    useContext(QueryRequestContext);
  const [requestId] = useState(() => crypto.randomUUID());
  const identity = useMemo(
    () => ({ requestId, coworldId, replayUri, elevatedPrivileges }),
    [requestId, coworldId, replayUri, elevatedPrivileges],
  );
  const launch = useLiveQuery((q) =>
    q.from({ launch: replayLaunchCollection(identity) }).findOne(),
  );
  const recordTiming = telemetry?.recordTiming;
  const setBadge = telemetry?.setBadge;
  const captureTerminal = telemetry?.captureTerminal;
  const onHealthPoll = useCallback(
    (healthPollCount: number) => recordTiming?.({ healthPollCount }),
    [recordTiming],
  );
  const created = launch.data;
  const readiness = useLiveQuery((q) =>
    created
      ? q
          .from({
            readiness: replayReadinessCollection({
              identity,
              launch: created,
              onHealthPoll,
            }),
          })
          .findOne()
      : undefined,
  );
  const readinessError = collectionError(readiness.health);
  const [slowStart, setSlowStart] = useState(false);
  const letterboxBg = useReplayLetterbox({ ready: readiness.data, letterbox });
  const slowStartTimer = useRef<number | undefined>(undefined);
  useEffect(() => {
    const cancellation = new AbortController();
    slowStartTimer.current = window.setTimeout(() => {
      if (!cancellation.signal.aborted) setSlowStart(true);
    }, 10_000);
    const requestStartedAt = performance.now();
    recordTiming?.(
      freshReplayTiming({
        requestStartedAt,
        hiddenSince:
          document.visibilityState === "hidden" ? requestStartedAt : null,
        sessionShared: false,
      }),
    );
    void launchReplaySession({
      db,
      identity,
      signal: cancellation.signal,
    }).catch((error: Error) => {
      if (cancellation.signal.aborted) return;

      clearTimeout(slowStartTimer.current);
      captureTerminal?.(replayTerminals.sessionError);
      setError?.(error.message);
    });
    return () => {
      cancellation.abort();
      clearTimeout(slowStartTimer.current);
    };
  }, [db, identity, recordTiming, captureTerminal, setError]);
  useEffect(() => {
    if (!created) return;

    const timing = replayLaunchTiming(created);
    recordTiming?.(timing);
    setBadge?.({ delivery: timing.delivery });
  }, [created, recordTiming, setBadge]);
  useEffect(() => {
    if (!readiness.data) return;

    clearTimeout(slowStartTimer.current);
    recordTiming?.({
      runtimeReadyAt: readiness.data.readyAt,
      healthPollCount: readiness.data.healthPollCount,
    });
  }, [readiness.data, recordTiming]);
  useEffect(() => {
    if (!readinessError) return;

    clearTimeout(slowStartTimer.current);

    const failure = replayReadinessFailure(readinessError);
    reportAuthorizationError(readinessError);
    void discardUnavailableReplayLaunch({
      db,
      identity,
      error: readinessError,
    });
    captureTerminal?.(failure.terminal);
    setError?.(failure.message);
  }, [
    db,
    identity,
    readinessError,
    reportAuthorizationError,
    captureTerminal,
    setError,
  ]);
  const session =
    readiness.data && !readinessError && letterboxBg !== undefined
      ? (created ?? null)
      : null;
  return { session, slowStart, letterboxBg: letterboxBg ?? null };
}
